import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Select,
  TextArea,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsCheckCircle, BsGlobe, BsEnvelope, BsPencil, BsSave } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";

const STATO_CFG = {
  bozza:      { cls: "bg-gray-100 text-gray-700",  label: "Bozza" },
  approvato:  { cls: "bg-blue-100 text-blue-800",   label: "Approvato" },
  pubblicato: { cls: "bg-green-100 text-green-800", label: "Pubblicato" },
};

function StatoBadge({ stato }) {
  const cfg = STATO_CFG[stato] ?? { cls: "", label: stato };
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function PunteggioRow({ valutazione, rank }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-3 px-4">
        <span className="mr-2">{medals[rank] ?? `#${rank + 1}`}</span>
        <span className="font-medium text-gray-900">
          {valutazione.propostaTitolo ?? `Proposta #${valutazione.propostaId}`}
        </span>
        {valutazione.operatoreEconomicoNome && (
          <div className="text-xs text-gray-500 mt-0.5">{valutazione.operatoreEconomicoNome}</div>
        )}
      </td>
      <td className="py-3 px-4 text-center">
        <span className="text-2xl font-bold text-blue-600">
          {valutazione.punteggioTotale != null ? valutazione.punteggioTotale.toFixed(2) : "—"}
        </span>
      </td>
      <td className="py-3 px-4">
        {(valutazione.punteggi ?? []).map((p, i) => (
          <div key={i} className="text-xs text-gray-600 flex justify-between">
            <span>{p.nome_criterio}</span>
            <span className="ml-4 font-medium">{p.punteggio}</span>
          </div>
        ))}
      </td>
      <td className="py-3 px-4 text-xs text-gray-500">{valutazione.noteValutazione || "—"}</td>
      <td className="py-3 px-4 text-xs text-gray-400">
        {valutazione.valutatoDaNome || "—"}
      </td>
    </tr>
  );
}

export default function VerbaleValutazioneDetail() {
  const { procedimentoId, verbaleId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useStateContext();
  const { can } = useAuthUser();
  const api = usePppApi();

  const [verbale, setVerbale] = useState(null);
  const [valutazioni, setValutazioni] = useState([]);
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [approvando, setApprovando] = useState(false);
  const [pubblicando, setPubblicando] = useState(false);
  const [invitando, setInvitando] = useState(false);

  const [noteApprovazione, setNoteApprovazione] = useState("");
  const [dataPubblicazione, setDataPubblicazione] = useState("");
  const [urlPubblicazione, setUrlPubblicazione] = useState("");
  const [dataInvito, setDataInvito] = useState("");
  const [noteInvito, setNoteInvito] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.getVerbaleValutazione(procedimentoId, verbaleId, {
      onSuccess: (data) => {
        setVerbale(data);
        setForm({
          propostaSelezionataId: data.propostaSelezionataId ? String(data.propostaSelezionataId) : "",
          numeroVerbale:         data.numeroVerbale ?? "",
          dataVerbale:           data.dataVerbale ?? "",
          motivazioneSelezione:  data.motivazioneSelezione ?? "",
          noteCommissione:       data.noteCommissione ?? "",
        });

        // Carica punteggi
        if (data.schemaValutazioneId) {
          api.listValutazioniComparative(procedimentoId, data.schemaValutazioneId, {
            onSuccess: (vals) => setValutazioni(vals),
          });
        }

        // Carica proposte per il selettore
        api.listProposte(procedimentoId, null, {
          onSuccess: (ps) => setProposte(ps),
        });

        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  }, [procedimentoId, verbaleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function handleSalva() {
    setSalvando(true);
    api.aggiornaVerbaleValutazione(procedimentoId, verbaleId, {
      propostaSelezionataId: form.propostaSelezionataId ? parseInt(form.propostaSelezionataId) : null,
      numeroVerbale:         form.numeroVerbale || null,
      dataVerbale:           form.dataVerbale || null,
      motivazioneSelezione:  form.motivazioneSelezione || null,
      noteCommissione:       form.noteCommissione || null,
    }, {
      onSuccess: () => {
        setSalvando(false);
        setEditMode(false);
        addToast({ title: "Verbale aggiornato", type: "success" });
        load();
      },
      onError: () => {
        setSalvando(false);
        addToast({ title: "Errore durante il salvataggio", type: "error" });
      },
    });
  }

  function handleApprova() {
    setApprovando(true);
    api.approvaVerbaleValutazione(procedimentoId, verbaleId, { noteApprovazione }, {
      onSuccess: () => {
        setApprovando(false);
        addToast({ title: "Verbale approvato", type: "success" });
        load();
      },
      onError: () => {
        setApprovando(false);
        addToast({ title: "Errore durante l\'approvazione", type: "error" });
      },
    });
  }

  function handlePubblica() {
    setPubblicando(true);
    api.pubblicaEsitoVerbale(procedimentoId, verbaleId, { dataPubblicazione, urlPubblicazione }, {
      onSuccess: () => {
        setPubblicando(false);
        addToast({ title: "Esito pubblicato", type: "success" });
        load();
      },
      onError: () => {
        setPubblicando(false);
        addToast({ title: "Errore durante la pubblicazione", type: "error" });
      },
    });
  }

  function handleInvitaModifiche() {
    setInvitando(true);
    api.invitaModificheVerbale(procedimentoId, verbaleId, { dataInvito, noteInvito }, {
      onSuccess: () => {
        setInvitando(false);
        addToast({ title: "Invito modifiche registrato", type: "success" });
        load();
      },
      onError: () => {
        setInvitando(false);
        addToast({ title: "Errore durante l\'invio", type: "error" });
      },
    });
  }

  if (loading) return <Spinner />;
  if (!verbale) return <div className="text-center py-8 text-gray-400">Verbale non trovato</div>;

  const isBozza      = verbale.stato === "bozza";
  const isApprovato  = verbale.stato === "approvato";
  const isPubblicato = verbale.stato === "pubblicato";

  const proposteOptions = [
    { value: "", label: "Nessuna selezionata" },
    ...proposte.map((p) => ({
      value: String(p.id),
      label: `${p.titolo ?? `Proposta #${p.id}`} — ${p.operatoreEconomicoNome ?? ""}`,
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <BsArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {verbale.numeroVerbale ?? `Verbale #${verbale.id}`}
            </h2>
            <StatoBadge stato={verbale.stato} />
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Valutazione Comparativa Finale — Fase 9 — art. 193, co. 5-6 D.lgs 36/2023
          </p>
        </div>
        {isBozza && !editMode && (
          <SimpleButton
            text={<><BsPencil className="inline mr-1" />Modifica</>}
            customFunc={() => setEditMode(true)}
          />
        )}
      </div>

      {/* Griglia punteggi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Classifica Proposte
        </h3>
        {valutazioni.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            Nessun punteggio inserito. Inserire i punteggi dalla scheda delle proposte.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="py-2 px-4">Proposta</th>
                  <th className="py-2 px-4 text-center">Punteggio</th>
                  <th className="py-2 px-4">Dettaglio criteri</th>
                  <th className="py-2 px-4">Note</th>
                  <th className="py-2 px-4">Valutato da</th>
                </tr>
              </thead>
              <tbody>
                {valutazioni.map((v, i) => (
                  <PunteggioRow key={v.id} valutazione={v} rank={i} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dati verbale */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Dati Verbale</h3>
        </div>

        {editMode ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Numero verbale</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.numeroVerbale}
                  onChange={(e) => setForm((f) => ({ ...f, numeroVerbale: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Data verbale</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.dataVerbale}
                  onChange={(e) => setForm((f) => ({ ...f, dataVerbale: e.target.value }))}
                />
              </div>
            </div>
            <Select
              label="Proposta selezionata come migliore"
              options={proposteOptions}
              value={form.propostaSelezionataId}
              onChange={(e) => setForm((f) => ({ ...f, propostaSelezionataId: e.target.value }))}
            />
            <TextArea
              label="Motivazione selezione"
              value={form.motivazioneSelezione}
              onChange={(e) => setForm((f) => ({ ...f, motivazioneSelezione: e.target.value }))}
              rows={4}
              placeholder="Illustrare i motivi per cui la proposta selezionata risulta la migliore..."
            />
            <TextArea
              label="Note commissione"
              value={form.noteCommissione}
              onChange={(e) => setForm((f) => ({ ...f, noteCommissione: e.target.value }))}
              rows={3}
            />
            <div className="flex gap-2 justify-end pt-2">
              <SimpleButton text="Annulla" customFunc={() => setEditMode(false)} />
              <LoadingButton
                isLoading={salvando}
                customFunc={handleSalva}
                text={<><BsSave className="inline mr-1" />Salva</>}
              />
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Numero verbale</dt>
              <dd className="font-medium text-gray-900">{verbale.numeroVerbale || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Data verbale</dt>
              <dd className="font-medium text-gray-900">{formatDate(verbale.dataVerbale)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Schema di valutazione</dt>
              <dd className="font-medium text-gray-900">{verbale.schemaTitolo || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Proposta selezionata</dt>
              <dd className="font-medium text-green-700">
                {verbale.propostaSelezionataTitolo
                  ? `${verbale.propostaSelezionataTitolo} (${verbale.operatoreSelezionatoNome ?? ""})`
                  : "Non ancora selezionata"}
              </dd>
            </div>
            {verbale.motivazioneSelezione && (
              <div className="col-span-2">
                <dt className="text-xs text-gray-500">Motivazione selezione</dt>
                <dd className="text-gray-700 mt-1 whitespace-pre-wrap">{verbale.motivazioneSelezione}</dd>
              </div>
            )}
            {verbale.noteCommissione && (
              <div className="col-span-2">
                <dt className="text-xs text-gray-500">Note commissione</dt>
                <dd className="text-gray-700 mt-1">{verbale.noteCommissione}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {/* Azioni di stato */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Approvazione */}
        <div className={`rounded-xl border p-4 ${isApprovato || isPubblicato ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center gap-2 mb-2">
            <BsCheckCircle className={isApprovato || isPubblicato ? "text-blue-500" : "text-gray-400"} size={16} />
            <span className="text-sm font-semibold text-gray-800">Approvazione</span>
          </div>
          {isApprovato || isPubblicato ? (
            <div className="text-xs text-gray-600 space-y-0.5">
              <div>Approvato da: <span className="font-medium">{verbale.approvatoDaNome}</span></div>
              <div>Il: <span className="font-medium">{formatDate(verbale.approvatoIl)}</span></div>
              {verbale.noteApprovazione && <div>Note: {verbale.noteApprovazione}</div>}
            </div>
          ) : can('ppp.verbale_valutazione.approve') ? (
            <div className="space-y-2">
              <TextArea
                label=""
                placeholder="Note di approvazione (facoltative)"
                value={noteApprovazione}
                onChange={(e) => setNoteApprovazione(e.target.value)}
                rows={2}
              />
              <LoadingButton
                isLoading={approvando}
                customFunc={handleApprova}
                text="Approva verbale"
                disabled={!verbale.propostaSelezionataId}
              />
              {!verbale.propostaSelezionataId && (
                <p className="text-xs text-amber-600">Selezionare prima la proposta migliore</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Permesso non sufficiente per approvare.</p>
          )}
        </div>

        {/* Pubblicazione esito */}
        <div className={`rounded-xl border p-4 ${isPubblicato ? "border-green-200 bg-green-50" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center gap-2 mb-2">
            <BsGlobe className={isPubblicato ? "text-green-500" : "text-gray-400"} size={16} />
            <span className="text-sm font-semibold text-gray-800">Pubblicazione esito</span>
          </div>
          {isPubblicato ? (
            <div className="text-xs text-gray-600 space-y-0.5">
              <div>Data: <span className="font-medium">{formatDate(verbale.dataPubblicazioneEsito)}</span></div>
              {verbale.urlPubblicazione && (
                <div>
                  URL: <a href={verbale.urlPubblicazione} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{verbale.urlPubblicazione}</a>
                </div>
              )}
            </div>
          ) : can('ppp.verbale_valutazione.publish') ? (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Data pubblicazione</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                  value={dataPubblicazione}
                  onChange={(e) => setDataPubblicazione(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">URL pubblicazione</label>
                <input
                  type="url"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                  placeholder="https://..."
                  value={urlPubblicazione}
                  onChange={(e) => setUrlPubblicazione(e.target.value)}
                />
              </div>
              <LoadingButton
                isLoading={pubblicando}
                customFunc={handlePubblica}
                text="Pubblica esito"
                disabled={!isApprovato || !dataPubblicazione}
              />
              {!isApprovato && (
                <p className="text-xs text-amber-600">Approvare prima il verbale</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Permesso non sufficiente per pubblicare l'esito.</p>
          )}
        </div>

        {/* Invito modifiche */}
        <div className={`rounded-xl border p-4 ${verbale.invitoModificheInviato ? "border-indigo-200 bg-indigo-50" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center gap-2 mb-2">
            <BsEnvelope className={verbale.invitoModificheInviato ? "text-indigo-500" : "text-gray-400"} size={16} />
            <span className="text-sm font-semibold text-gray-800">Invito modifiche</span>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Art. 193, co. 6 — Invito al Promotore ad apportare le modifiche necessarie
          </p>
          {verbale.invitoModificheInviato ? (
            <div className="text-xs text-gray-600 space-y-0.5">
              <div>Data: <span className="font-medium">{formatDate(verbale.dataInvitoModifiche)}</span></div>
              {verbale.noteInvitoModifiche && <div>Note: {verbale.noteInvitoModifiche}</div>}
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Data invito</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                  value={dataInvito}
                  onChange={(e) => setDataInvito(e.target.value)}
                />
              </div>
              <TextArea
                label=""
                placeholder="Note sull'invito (facoltative)"
                value={noteInvito}
                onChange={(e) => setNoteInvito(e.target.value)}
                rows={2}
              />
              <LoadingButton
                isLoading={invitando}
                customFunc={handleInvitaModifiche}
                text="Registra invito"
                disabled={!isApprovato || !dataInvito}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
